import { forwardRef, useState, type ComponentProps, type ComponentType, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Check, Eye, EyeOff, Lock } from 'lucide-react'

import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

type IconComponent = ComponentType<{ className?: string }>

export type SignupHighlight = { icon: IconComponent; title: string; text: string }
export type SignupTrustPoint = { icon: IconComponent; label: string }

export type SignupBrand = {
  logo: ReactNode
  mobileLogo: ReactNode
  eyebrow: string
  headline: string
  description: string
  highlights: SignupHighlight[]
  trustPoints: SignupTrustPoint[]
  gradientClassName: string
  backdrop?: ReactNode
}

export type PasswordRule = { label: string; test: (value: string) => boolean }

export function SignupSplitShell({
  brand,
  title,
  description,
  children,
}: {
  brand: SignupBrand
  title: string
  description: string
  children: ReactNode
}) {
  return (
    <div className="min-h-screen bg-slate-50 lg:grid lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
      <aside className={cn('relative isolate hidden overflow-hidden bg-gradient-to-br text-white lg:flex lg:flex-col lg:justify-between lg:p-12 xl:p-16', brand.gradientClassName)}>
        {brand.backdrop}
        <div aria-hidden className="absolute -left-24 -top-24 h-96 w-96 rounded-full bg-white/10 blur-3xl" />
        <div aria-hidden className="absolute -bottom-32 -right-20 h-[28rem] w-[28rem] rounded-full bg-white/10 blur-3xl" />
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.07]"
          style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '22px 22px' }}
        />

        <div className="relative">{brand.logo}</div>

        <div className="relative max-w-lg py-12">
          <p className="text-xs font-bold uppercase tracking-[0.24em] text-white/70">{brand.eyebrow}</p>
          <h1 className="mt-4 text-4xl font-black leading-[1.08] tracking-tight xl:text-5xl">{brand.headline}</h1>
          <p className="mt-5 text-base leading-7 text-white/80">{brand.description}</p>

          <ul className="mt-10 grid gap-5 sm:grid-cols-2">
            {brand.highlights.map(({ icon: Icon, title: highlightTitle, text }) => (
              <li key={highlightTitle} className="flex gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/15 ring-1 ring-white/25">
                  <Icon className="h-5 w-5" />
                </span>
                <div>
                  <p className="text-sm font-semibold">{highlightTitle}</p>
                  <p className="mt-0.5 text-xs leading-5 text-white/70">{text}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="relative flex flex-wrap gap-x-6 gap-y-2 text-xs font-medium text-white/75">
          {brand.trustPoints.map(({ icon: Icon, label }) => (
            <span key={label} className="flex items-center gap-1.5"><Icon className="h-3.5 w-3.5" /> {label}</span>
          ))}
        </div>
      </aside>

      <main className="flex min-h-screen flex-col px-5 py-8 sm:px-10 lg:px-14 lg:py-12">
        <div className="flex items-center justify-between gap-4">
          <div className="lg:hidden">{brand.mobileLogo}</div>
          <p className="ml-auto text-sm text-muted-foreground">
            Already have an account?{' '}
            <Link to="/login" className="font-semibold text-primary hover:underline">
              Sign in
            </Link>
          </p>
        </div>

        <div className="mx-auto flex w-full max-w-xl flex-1 flex-col justify-center py-10">
          <div className="mb-8">
            <h2 className="text-3xl font-black tracking-tight text-slate-900">{title}</h2>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">{description}</p>
          </div>
          {children}
        </div>
      </main>
    </div>
  )
}

export function FormSection({ step, title, children }: { step: number; title: string; children: ReactNode }) {
  return (
    <fieldset className="space-y-4">
      <legend className="mb-4 flex items-center gap-3">
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
          {step}
        </span>
        <span className="text-sm font-semibold tracking-tight text-slate-900">{title}</span>
      </legend>
      {children}
    </fieldset>
  )
}

export function FieldError({ message }: { message?: string }) {
  return message ? <p className="text-xs font-medium text-destructive">{message}</p> : null
}

type IconInputProps = ComponentProps<typeof Input> & { icon: IconComponent }

export const IconInput = forwardRef<HTMLInputElement, IconInputProps>(({ icon: Icon, className, ...props }, ref) => (
  <div className="relative">
    <Icon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
    <Input ref={ref} className={cn('h-11 rounded-xl bg-white pl-10', className)} {...props} />
  </div>
))
IconInput.displayName = 'IconInput'

type PhoneInputProps = ComponentProps<'input'> & { dialCode?: string | null }

export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(({ dialCode, className, ...props }, ref) => {
  const normalizedDialCode = String(dialCode ?? '').replace(/^\+/, '')
  return (
    <div className="flex h-11 overflow-hidden rounded-xl border border-input bg-white shadow-sm focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50 has-[[aria-invalid=true]]:border-destructive">
      <span className="flex min-w-14 items-center justify-center border-r bg-slate-50 px-3 text-sm font-medium text-slate-600">
        +{normalizedDialCode}
      </span>
      <input
        ref={ref}
        type="tel"
        autoComplete="tel-national"
        className={cn('w-full min-w-0 bg-transparent px-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50', className)}
        {...props}
      />
    </div>
  )
})
PhoneInput.displayName = 'PhoneInput'

export const PasswordInput = forwardRef<HTMLInputElement, ComponentProps<typeof Input>>(({ className, ...props }, ref) => {
  const [visible, setVisible] = useState(false)
  return (
    <div className="relative">
      <IconInput ref={ref} icon={Lock} type={visible ? 'text' : 'password'} autoComplete="new-password" className={cn('pr-10', className)} {...props} />
      <button
        type="button"
        onClick={() => setVisible((current) => !current)}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
        aria-label={visible ? 'Hide password' : 'Show password'}
      >
        {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  )
})
PasswordInput.displayName = 'PasswordInput'

export function PasswordChecklist({ rules, value, showErrors }: { rules: readonly PasswordRule[]; value: string; showErrors: boolean }) {
  return (
    <ul className="grid gap-x-4 gap-y-1.5 rounded-xl border border-slate-200 bg-white px-4 py-3 sm:grid-cols-2" aria-label="Password requirements">
      {rules.map((rule) => {
        const passed = rule.test(value)
        const failed = !passed && showErrors
        return (
          <li key={rule.label} className={cn('flex items-center gap-2 text-xs transition-colors', passed ? 'text-emerald-600' : failed ? 'text-destructive' : 'text-muted-foreground')}>
            <span className={cn('flex h-4 w-4 items-center justify-center rounded-full', passed ? 'bg-emerald-500 text-white' : failed ? 'border border-destructive' : 'border border-slate-300')}>
              {passed ? <Check className="h-3 w-3" strokeWidth={3} /> : null}
            </span>
            {rule.label}
          </li>
        )
      })}
    </ul>
  )
}
